import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  Grid,
  Stack,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import RuleIcon from '@mui/icons-material/Rule';
import CategoryIcon from '@mui/icons-material/Category';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import axios from 'axios';
import { ConfirmDialog } from './common/ConfirmDialog';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface Division {
  division_id: number;
  division_name: string;
}

type PatternType = 'exact' | 'contains' | 'regex';

interface DivisionMapping {
  mapping_id: number;
  division_id: number;
  division_name: string;
  pattern: string;
  pattern_type: PatternType;
  priority: number;
  notes: string | null;
}

interface FormData {
  division_id: number | '';
  pattern: string;
  pattern_type: PatternType;
  priority: number;
  notes: string;
}

interface AlertState {
  message: string;
  severity: 'info' | 'success' | 'warning' | 'error';
}

const PATTERN_LABEL: Record<PatternType, string> = {
  exact: 'Exact',
  contains: 'Contains',
  regex: 'Regex',
};

const PATTERN_TONE: Record<PatternType, 'primary' | 'default' | 'secondary'> = {
  exact: 'primary',
  contains: 'default',
  regex: 'secondary',
};

const emptyForm: FormData = {
  division_id: '',
  pattern: '',
  pattern_type: 'contains',
  priority: 100,
  notes: '',
};

function DivisionMappingManagement(): React.ReactElement {
  const [mappings, setMappings] = useState<DivisionMapping[]>([]);
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState<DivisionMapping | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DivisionMapping | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [alert, setAlert] = useState<AlertState | null>(null);
  const alertTimerRef = useRef<number | null>(null);

  const loadData = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const [mappingsResponse, divisionsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/division-mappings`),
        axios.get(`${API_BASE_URL}/divisions`),
      ]);
      setMappings(mappingsResponse.data);
      setDivisions(divisionsResponse.data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      showAlert(`Error loading data: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    return () => {
      if (alertTimerRef.current !== null) {
        window.clearTimeout(alertTimerRef.current);
      }
    };
  }, [loadData]);

  const showAlert = (
    message: string,
    severity: 'info' | 'success' | 'warning' | 'error' = 'info',
  ): void => {
    setAlert({ message, severity });
    if (alertTimerRef.current !== null) {
      window.clearTimeout(alertTimerRef.current);
    }
    alertTimerRef.current = window.setTimeout(() => {
      setAlert(null);
      alertTimerRef.current = null;
    }, 5000);
  };

  const handleOpenAdd = (): void => {
    setEditingMapping(null);
    setFormData(emptyForm);
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (mapping: DivisionMapping): void => {
    setEditingMapping(mapping);
    setFormData({
      division_id: mapping.division_id,
      pattern: mapping.pattern,
      pattern_type: mapping.pattern_type,
      priority: mapping.priority,
      notes: mapping.notes || '',
    });
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = (): void => {
    if (isSaving) return;
    setDialogOpen(false);
    setEditingMapping(null);
    setDialogError(null);
  };

  const handleSubmit = async (): Promise<void> => {
    if (!formData.division_id) {
      setDialogError('Pick a division.');
      return;
    }
    if (!formData.pattern.trim()) {
      setDialogError('Pattern is required.');
      return;
    }

    setIsSaving(true);
    try {
      if (editingMapping) {
        await axios.put(
          `${API_BASE_URL}/division-mappings/${editingMapping.mapping_id}`,
          formData,
        );
        showAlert('Mapping updated', 'success');
      } else {
        await axios.post(`${API_BASE_URL}/division-mappings`, formData);
        showAlert('Mapping created', 'success');
      }
      setDialogOpen(false);
      setEditingMapping(null);
      loadData();
    } catch (err) {
      const axiosError = err as { response?: { data?: { error?: string } }; message: string };
      setDialogError(axiosError.response?.data?.error || axiosError.message || 'Save failed.');
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API_BASE_URL}/division-mappings/${deleteTarget.mapping_id}`);
      setMappings((prev) => prev.filter((m) => m.mapping_id !== deleteTarget.mapping_id));
      showAlert('Mapping deleted', 'success');
    } catch (err) {
      const axiosError = err as { response?: { data?: { error?: string } }; message: string };
      showAlert(
        `Error deleting mapping: ${axiosError.response?.data?.error || axiosError.message}`,
        'error',
      );
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  const groupedMappings = useMemo(() => {
    const grouped = new Map<string, DivisionMapping[]>();
    mappings.forEach((m) => {
      const key = m.division_name;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(m);
    });
    return Array.from(grouped.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, list]) => [name, list.slice().sort((a, b) => a.priority - b.priority)] as const);
  }, [mappings]);

  const stats = useMemo(() => {
    const mappedDivisions = new Set(mappings.map((m) => m.division_id));
    const unmappedCount = divisions.filter((d) => !mappedDivisions.has(d.division_id)).length;
    return [
      { label: 'Total Mappings', value: mappings.length.toLocaleString(), icon: <RuleIcon /> },
      {
        label: 'Mapped Divisions',
        value: mappedDivisions.size.toLocaleString(),
        icon: <CategoryIcon />,
      },
      {
        label: 'Unmapped Divisions',
        value: unmappedCount.toLocaleString(),
        icon: <ReportProblemOutlinedIcon />,
        tone: unmappedCount > 0 ? ('warning' as const) : undefined,
      },
    ];
  }, [mappings, divisions]);

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1">
            Division Mappings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Map MyRacePass division names to local divisions during import. Lower priority numbers
            run first.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            onClick={loadData}
          >
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>
            Add Mapping
          </Button>
        </Box>
      </Box>

      {alert && (
        <Alert severity={alert.severity} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat) => (
          <Grid key={stat.label} item xs={12} sm={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: '0.04em', fontWeight: 500, lineHeight: 1.2 }}
                  >
                    {stat.label}
                  </Typography>
                  <Box
                    sx={{
                      color: stat.tone === 'warning' ? 'warning.main' : 'text.disabled',
                      display: 'flex',
                      alignItems: 'center',
                      '& svg': { fontSize: 18 },
                    }}
                  >
                    {stat.icon}
                  </Box>
                </Box>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}
                >
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="240px">
          <CircularProgress />
        </Box>
      ) : groupedMappings.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No division mappings yet. Add one to map MyRacePass division names.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {groupedMappings.map(([divisionName, divisionMappings]) => (
            <Paper key={divisionName} sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {divisionName}
                </Typography>
                <Chip
                  label={`${divisionMappings.length} ${
                    divisionMappings.length === 1 ? 'mapping' : 'mappings'
                  }`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 90 }}>Priority</TableCell>
                      <TableCell>Pattern</TableCell>
                      <TableCell sx={{ width: 110 }}>Type</TableCell>
                      <TableCell>Notes</TableCell>
                      <TableCell sx={{ width: 100 }} align="right">
                        {''}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {divisionMappings.map((mapping) => (
                      <TableRow key={mapping.mapping_id}>
                        <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {mapping.priority}
                        </TableCell>
                        <TableCell>
                          <Box
                            component="code"
                            sx={{
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                              fontSize: '0.8125rem',
                              backgroundColor: 'action.hover',
                              color: 'text.primary',
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 0.5,
                            }}
                          >
                            {mapping.pattern}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={PATTERN_LABEL[mapping.pattern_type]}
                            color={PATTERN_TONE[mapping.pattern_type]}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {mapping.notes || '—'}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEdit(mapping)}
                                sx={{
                                  color: 'text.secondary',
                                  '&:hover, &.Mui-focusVisible': { color: 'primary.main' },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => setDeleteTarget(mapping)}
                                sx={{
                                  color: 'text.secondary',
                                  '&:hover, &.Mui-focusVisible': { color: 'error.main' },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ))}
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingMapping ? 'Edit Mapping' : 'Add Mapping'}</DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError(null)}>
              {dialogError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Division</InputLabel>
                <Select
                  value={formData.division_id}
                  onChange={(e: SelectChangeEvent<number | ''>) =>
                    setFormData({ ...formData, division_id: e.target.value as number | '' })
                  }
                  label="Division"
                >
                  {divisions.map((division) => (
                    <MenuItem key={division.division_id} value={division.division_id}>
                      {division.division_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                size="small"
                label="Pattern"
                value={formData.pattern}
                onChange={(e) => setFormData({ ...formData, pattern: e.target.value })}
                placeholder="e.g. UMP PRO MODIFIED"
                helperText="Text to match against MyRacePass division names"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.pattern_type}
                  onChange={(e: SelectChangeEvent<PatternType>) =>
                    setFormData({ ...formData, pattern_type: e.target.value as PatternType })
                  }
                  label="Type"
                >
                  <MenuItem value="exact">Exact</MenuItem>
                  <MenuItem value="contains">Contains</MenuItem>
                  <MenuItem value="regex">Regex</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Priority"
                type="number"
                value={formData.priority}
                onChange={(e) =>
                  setFormData({ ...formData, priority: parseInt(e.target.value, 10) || 100 })
                }
                helperText="Lower numbers run first"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Optional"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={handleCloseDialog} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSaving || !formData.division_id || !formData.pattern.trim()}
          >
            {isSaving ? 'Saving' : editingMapping ? 'Save changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete Mapping"
        message={
          deleteTarget
            ? `Delete the "${deleteTarget.pattern}" mapping for ${deleteTarget.division_name}?`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        severity="error"
        loading={isDeleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}

export default DivisionMappingManagement;
